import { motion } from "framer-motion";
import { Phone, Mic, Loader2 } from "lucide-react";
import { CallStatus } from "@/hooks/use-vapi-demo";

interface AnimatedVoiceOrbProps {
  status: CallStatus;
  volumeLevel: number;
  isMuted: boolean;
}

export function AnimatedVoiceOrb({ status, volumeLevel, isMuted }: AnimatedVoiceOrbProps) {
  const isActive = status === "active";
  const isConnecting = status === "connecting" || status === "generating";
  const isListening = isActive && volumeLevel > 20;

  // Color based on state - using lime/green design system
  const getOrbColors = () => {
    if (status === "error") return { from: "#ef4444", to: "#dc2626" };
    if (status === "ended") return { from: "#6b7280", to: "#4b5563" };
    if (isListening) return { from: "#a3e635", to: "#65a30d" }; // Lime-300 to lime-600 when user speaking
    if (isActive) return { from: "#84cc16", to: "#65a30d" }; // Lime when active
    if (isConnecting) return { from: "#f59e0b", to: "#d97706" }; // Amber
    return { from: "#6b7280", to: "#4b5563" }; // Gray idle
  };

  const colors = getOrbColors();

  // Animation variants for the orb
  const orbVariants = {
    idle: {
      scale: 1,
      boxShadow: "0 0 20px rgba(107, 114, 128, 0.3)",
    },
    connecting: {
      scale: [1, 1.05, 1],
      boxShadow: [
        "0 0 30px rgba(245, 158, 11, 0.4)",
        "0 0 50px rgba(245, 158, 11, 0.6)",
        "0 0 30px rgba(245, 158, 11, 0.4)",
      ],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
    active: {
      scale: 1 + volumeLevel * 0.003,
      boxShadow: `0 0 ${40 + volumeLevel}px rgba(132, 204, 22, ${0.4 + volumeLevel * 0.006})`,
      transition: {
        duration: 0.1,
        ease: "easeOut",
      },
    },
    listening: {
      scale: 1 + volumeLevel * 0.004,
      boxShadow: `0 0 ${50 + volumeLevel * 1.5}px rgba(163, 230, 53, ${0.5 + volumeLevel * 0.005})`,
      transition: {
        duration: 0.05,
        ease: "easeOut",
      },
    },
    ended: {
      scale: 0.95,
      boxShadow: "0 0 20px rgba(107, 114, 128, 0.3)",
    },
    error: {
      scale: 1,
      boxShadow: "0 0 30px rgba(239, 68, 68, 0.5)",
      x: [0, -5, 5, -5, 5, 0],
      transition: {
        x: { duration: 0.4 },
      },
    },
  };

  const getAnimationState = () => {
    if (status === "error") return "error";
    if (status === "ended") return "ended";
    if (isConnecting) return "connecting";
    if (isListening) return "listening";
    if (isActive) return "active";
    return "idle";
  };

  // Pulse ring variants
  const ringVariants = {
    hidden: { scale: 1, opacity: 0 },
    visible: {
      scale: [1, 1.5, 2],
      opacity: [0.6, 0.3, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="relative">
      {/* Pulse rings - only visible when active and user speaking */}
      {isListening && (
        <>
          <motion.div
            variants={ringVariants}
            initial="hidden"
            animate="visible"
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: colors.from }}
          />
          <motion.div
            variants={ringVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: colors.from }}
          />
        </>
      )}

      {/* Ambient glow behind orb */}
      {isActive && (
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute inset-0 rounded-full blur-xl"
          style={{
            background: `radial-gradient(circle, ${colors.from}40 0%, transparent 70%)`,
          }}
        />
      )}

      {/* Main orb */}
      <motion.div
        variants={orbVariants}
        initial="idle"
        animate={getAnimationState()}
        className="relative w-48 h-48 rounded-full flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)`,
        }}
      >
        {/* Inner gradient overlay */}
        <div
          className="absolute inset-2 rounded-full opacity-30"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 50%)",
          }}
        />

        {/* Icon */}
        <div className="relative z-10">
          {isConnecting ? (
            <Loader2 className="w-20 h-20 text-white animate-spin" />
          ) : isMuted ? (
            <Mic className="w-20 h-20 text-white/50" />
          ) : (
            <Phone className="w-20 h-20 text-white" />
          )}
        </div>

        {/* Volume level indicator (subtle inner ring) */}
        {isActive && volumeLevel > 5 && (
          <motion.div
            className="absolute inset-4 rounded-full border-4 border-white/30"
            animate={{
              scale: 1 + volumeLevel * 0.002,
              opacity: 0.3 + volumeLevel * 0.007,
            }}
            transition={{ duration: 0.05 }}
          />
        )}
      </motion.div>

      {/* Status indicator dot */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
        <motion.div
          animate={{
            scale: isActive ? [1, 1.2, 1] : 1,
          }}
          transition={{
            duration: 1,
            repeat: isActive ? Infinity : 0,
          }}
          className={`w-3 h-3 rounded-full ${
            status === "active"
              ? "bg-lime-500"
              : status === "error"
              ? "bg-red-500"
              : isConnecting
              ? "bg-amber-500"
              : "bg-gray-400"
          }`}
        />
      </div>
    </div>
  );
}
